import convertSystem from './convertSystem';

export interface WordOption {
    char: string;
    jp: string;
    trans?: string;
    [language: string]: string | undefined;
}

/** The first item is the selected option index (legacy saved-data format). */
export type WordChunk = [number, ...WordOption[]];
export type WordLine = WordChunk[];
export interface ExportLine { name: string; words: string; }

export const selectedWord = (chunk: WordChunk): WordOption => chunk[chunk[0]] as WordOption;

export const selectWordOption = (chunk: WordChunk, option: number): WordChunk => {
    if (!Number.isInteger(option) || option < 1 || option >= chunk.length) {
        throw new Error('Invalid word option');
    }
    return [option, ...chunk.slice(1) as WordOption[]];
};

export const editWordOption = (
    chunk: WordChunk, option: number, field: string, value: string,
): WordChunk => {
    selectWordOption(chunk, option); // Validate before updating.
    return [chunk[0], ...(chunk.slice(1) as WordOption[]).map((word, index) => (
        index + 1 === option ? { ...word, [field]: value } : word
    ))];
};

export const serializeWordLine = (line: WordLine, languageOrder: string): string => line.map((chunk) => {
    const word = selectedWord(chunk);
    const gloss = word.trans || languageOrder.split(',').filter(Boolean)
        .map((language) => word[language] || '').join('#') || '#';
    return [word.char, word.jp, convertSystem(word.jp, 'yale'), gloss].join('|');
}).join('\n');

export const exportCode = (lines: ExportLine[]): string => lines.map(({ name, words }) => (
    `[${name}]${words.split(/\r?\n/).filter(Boolean).map((word) => `<<${word}>>`).join('')}`
)).join('\n');

export const exportTSV = (lines: ExportLine[]): string => lines.map(({ name, words }) => {
    const rows = words.split(/\r?\n/).filter(Boolean).map((word) => word.split('|'));
    return [`[${name}]`, ...[0, 1, 3].map((column) => (
        rows.map((row) => row[column] || '').join('\t')
    ))].join('\n');
}).join('\n');

/** Import the four-line format emitted by exportTSV, including empty glosses. */
export const importTSV = (text: string): string => {
    const lines = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n');
    // An optional final newline is not an additional record.
    if (lines.length % 4 === 1 && lines[lines.length - 1] === '') lines.pop();
    if (lines.length % 4 !== 0) throw new Error('TSV must contain four lines per panel.');
    const panels: string[] = [];
    for (let i = 0; i < lines.length; i += 4) {
        const [name, source, reading, gloss] = lines.slice(i, i + 4);
        const chars = source.replace(/\t</g, '').replace(/\|/g, '\t').split('\t');
        const readings = reading.replace(/\t</g, '-').replace(/\|/g, '\t').split('\t');
        const glosses = gloss.replace(/\t</g, ' ').replace(/\|/g, '\t').split('\t');
        if (chars.length !== readings.length || chars.length !== glosses.length) {
            throw new Error(`TSV panel ${i / 4 + 1} has mismatched columns.`);
        }
        const words = chars.map((char, index) => {
            const jp = readings[index].trim();
            return char.trim() ? `<<${char.trim()}|${jp}|${convertSystem(jp, 'yale')}|${glosses[index].trim()}>>` : '';
        });
        panels.push(`${name.trim()}${words.join('')}`);
    }
    return panels.join('\n');
};
